import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { trackMiniAppEvent } from '../../utils/analytics';
import {
  feedbackCorrect,
  feedbackIncorrect,
  secondaryBtn,
} from './shared/engineTheme';
import { shouldSuppressActivityTitle } from './shared/guidedPromptUtils';
import { useCelebration, CelebrationOverlay, A11yAnnouncement } from './shared/Celebration';
import { useGuidedHints } from './shared/useGuidedHints';
import GuidedHintOverlay, {
  GuidedHintHelpButton,
  guidedHintReadAloud,
} from './shared/GuidedHintOverlay';
import { buildOneShotInteractionHint } from './shared/dragDropMechanicHints';

const ACCENT = '#534AB7';
const ACCENT_RING = 'rgba(83,74,183,0.22)';
const ACCENT_DASHED = 'rgba(83,74,183,0.4)';

/**
 * Derive a card background color for a zone by finding which background rect
 * in scene.background contains the zone's center point (viewBox coordinates).
 */
function getZoneBgColor(zone, backgrounds = []) {
  const cx = (zone.x ?? 0) + (zone.width ?? 0) / 2;
  const cy = (zone.y ?? 0) + (zone.height ?? 0) / 2;
  const bg = backgrounds.find(
    (b) =>
      b.type === 'rect' &&
      cx >= (b.x ?? 0) &&
      cx <= (b.x ?? 0) + (b.width ?? 0) &&
      cy >= (b.y ?? 0) &&
      cy <= (b.y ?? 0) + (b.height ?? 0),
  );
  return bg?.fill || '#e0f9ff';
}

/**
 * Canvas tap-select-tap-place engine.
 *
 * Unified layout for all screen sizes and input types:
 *   1. Zones grid  (CSS 2×2, flex:1, fills available height)
 *   2. Animal tray (flexShrink:0, always visible)
 *
 * Tap animal → select (purple ring).
 * Tap zone   → place selected animal (swap if zone occupied).
 * Tap animal in zone → return to tray.
 * Footer Check validates all placements at once.
 *
 * Content shape:
 * {
 *   instructions?: string,   — suppressed in guided lesson context (singleSurface)
 *   scene: {
 *     background?: [{ type:'rect', x, y, width, height, fill }],
 *     zones: [{ id, label, x, y, width, height, fill? }],
 *   },
 *   pieces: [{ id, label, emoji?, homeZoneId }],
 *   victory?: { message? },
 * }
 */
export default function CanvasManipulativeEngine({
  title = 'Place each item',
  content = {},
  hints = [],
  explanation,
  activityId = 'engine_canvas_manipulative',
  onProgress,
  onComplete,
  guidedHintsEnabled = true,
  onRegisterLessonFooterActions,
  guidedContext,
}) {
  const singleSurface = guidedContext?.singleSurface === true;
  const stepTitle = typeof guidedContext?.stepTitle === 'string' ? guidedContext.stepTitle : '';

  const scene = content.scene;
  const pieces = useMemo(() => content.pieces || [], [content.pieces]);
  const multiplePerZone = content.multiplePerZone ?? false;
  const instructions = content.instructions || '';
  const victoryMsg = content.victory?.message || 'All animals are home!';
  const backgrounds = scene?.background || [];
  const zones = useMemo(() => scene?.zones || [], [scene]);

  // ── State ──────────────────────────────────────────────────────────────
  const [placed, setPlaced] = useState({});           // pieceId → zoneId
  const [selectedAnimal, setSelectedAnimal] = useState(null); // pieceId | null
  const [finished, setFinished] = useState(false);
  const [lastCheck, setLastCheck] = useState(null);   // 'correct'|'wrong'|null
  const [missCount, setMissCount] = useState(0);
  const [a11yMsg, setA11yMsg] = useState('');
  const [showCelebration, celebrate] = useCelebration();
  const completedRef = useRef(false);
  const revealAnswerRef = useRef(() => {});

  const allPlaced = pieces.length > 0 && pieces.every((p) => placed[p.id]);

  const wrongPieceIds = useMemo(() => {
    if (lastCheck !== 'wrong') return new Set();
    return new Set(pieces.filter((p) => placed[p.id] !== p.homeZoneId).map((p) => p.id));
  }, [lastCheck, pieces, placed]);

  const sceneOk = !!scene && Array.isArray(scene.zones) && scene.zones.length > 0 && pieces.length > 0;

  const showTitle =
    typeof title === 'string' &&
    title.trim() &&
    !singleSurface &&
    !shouldSuppressActivityTitle({ stepTitle, activityTitle: title });

  const getExplanation = useCallback(() => content.explanation, [content.explanation]);

  const oneShotInteractionHint = useMemo(
    () => buildOneShotInteractionHint(true, { canvasStyle: true }),
    [],
  );

  const guided = useGuidedHints({
    activityId,
    hints,
    oneShotInteractionHint,
    explanation,
    getExplanation,
    isActivityComplete: finished,
    enabled: guidedHintsEnabled,
  });

  const { noteUserAction, noteIncorrectAnswer, noteCorrectAnswer, resetSession } = guided;

  // ── Handlers ───────────────────────────────────────────────────────────

  /**
   * Tap a piece in the tray (or its slot when placed in a zone).
   * - In zone → return to tray
   * - In tray, same as selected → deselect
   * - In tray, different → select
   */
  const handleTrayTap = useCallback(
    (pieceId) => {
      if (finished) return;
      noteUserAction();
      setLastCheck(null);
      if (placed[pieceId]) {
        // Return from zone to tray
        setPlaced((p) => {
          const n = { ...p };
          delete n[pieceId];
          return n;
        });
        setSelectedAnimal(null);
        setA11yMsg(`${pieces.find((p) => p.id === pieceId)?.label || pieceId} returned to tray.`);
        onProgress?.({ activityId, engine: 'canvas_manipulative', phase: 'return_to_tray', pieceId });
        trackMiniAppEvent.interaction(activityId, 'return_to_tray');
      } else {
        setSelectedAnimal((cur) => (cur === pieceId ? null : pieceId));
        const label = pieces.find((p) => p.id === pieceId)?.label || pieceId;
        setA11yMsg(
          selectedAnimal === pieceId ? `${label} deselected.` : `${label} selected. Tap a habitat zone.`,
        );
      }
    },
    [finished, placed, pieces, selectedAnimal, activityId, onProgress, noteUserAction],
  );

  /**
   * Tap a habitat zone card.
   * - No animal selected → ignore
   * - multiplePerZone=true: always place; multiple animals can share a zone
   * - multiplePerZone=false: swap if occupied, deselect if same animal already there
   */
  const handleZoneTap = useCallback(
    (zoneId) => {
      if (!selectedAnimal || finished) return;
      noteUserAction();
      setLastCheck(null);

      if (multiplePerZone) {
        if (placed[selectedAnimal] === zoneId) {
          setSelectedAnimal(null);
          return;
        }
        setPlaced((p) => ({ ...p, [selectedAnimal]: zoneId }));
      } else {
        const prevInZone = Object.keys(placed).find((pid) => placed[pid] === zoneId);
        if (prevInZone === selectedAnimal) {
          setSelectedAnimal(null);
          return;
        }
        setPlaced((p) => {
          const n = { ...p };
          if (prevInZone) delete n[prevInZone];
          n[selectedAnimal] = zoneId;
          return n;
        });
      }

      const animalLabel = pieces.find((p) => p.id === selectedAnimal)?.label || selectedAnimal;
      const zoneLabel = zones.find((z) => z.id === zoneId)?.label || zoneId;
      setA11yMsg(`${animalLabel} placed in ${zoneLabel}.`);
      setSelectedAnimal(null);
      onProgress?.({
        activityId,
        engine: 'canvas_manipulative',
        phase: 'place',
        pieceId: selectedAnimal,
        zoneId,
      });
      trackMiniAppEvent.interaction(activityId, 'place');
    },
    [selectedAnimal, finished, placed, pieces, zones, activityId, onProgress, noteUserAction, multiplePerZone],
  );

  const checkPlacements = useCallback(() => {
    if (finished || !allPlaced) return;
    noteUserAction();
    const allCorrect = pieces.every((p) => placed[p.id] === p.homeZoneId);
    setLastCheck(allCorrect ? 'correct' : 'wrong');
    if (allCorrect) {
      if (!completedRef.current) {
        completedRef.current = true;
        setFinished(true);
        celebrate();
        noteCorrectAnswer();
        setA11yMsg(victoryMsg);
        onComplete?.({ activityId, engine: 'canvas_manipulative', perfect: missCount === 0, misses: missCount });
        trackMiniAppEvent.completed(activityId);
      }
    } else {
      setMissCount((c) => c + 1);
      noteIncorrectAnswer();
      setA11yMsg('Not quite — adjust and try again.');
      onProgress?.({ activityId, engine: 'canvas_manipulative', phase: 'check_wrong' });
      trackMiniAppEvent.interaction(activityId, 'check_wrong');
    }
  }, [
    finished,
    allPlaced,
    pieces,
    placed,
    missCount,
    activityId,
    victoryMsg,
    celebrate,
    noteUserAction,
    noteCorrectAnswer,
    noteIncorrectAnswer,
    onComplete,
    onProgress,
  ]);

  revealAnswerRef.current = useCallback(() => {
    if (!sceneOk || finished) return;
    noteUserAction();
    const next = {};
    for (const p of pieces) {
      if (p.homeZoneId) next[p.id] = p.homeZoneId;
    }
    setPlaced(next);
    setSelectedAnimal(null);
    setLastCheck(null);
    if (!completedRef.current) {
      completedRef.current = true;
      setFinished(true);
      celebrate();
      noteCorrectAnswer();
      setA11yMsg(victoryMsg);
      onComplete?.({ activityId, engine: 'canvas_manipulative', perfect: false, misses: missCount + 1 });
      trackMiniAppEvent.interaction(activityId, 'reveal_answer');
      trackMiniAppEvent.completed(activityId);
    }
    onProgress?.({ activityId, engine: 'canvas_manipulative', phase: 'reveal_answer' });
  }, [sceneOk, finished, pieces, celebrate, noteUserAction, noteCorrectAnswer, victoryMsg, onComplete, onProgress, activityId, missCount]);

  const resetAll = useCallback(() => {
    resetSession();
    completedRef.current = false;
    setPlaced({});
    setSelectedAnimal(null);
    setFinished(false);
    setLastCheck(null);
    setMissCount(0);
    setA11yMsg('');
  }, [resetSession]);

  // ── Footer registration ─────────────────────────────────────────────────
  useEffect(() => {
    if (!onRegisterLessonFooterActions) return undefined;
    if (finished) {
      onRegisterLessonFooterActions(null);
      return undefined;
    }
    onRegisterLessonFooterActions({
      checkAnswer: checkPlacements,
      checkAnswerDisabled: !allPlaced,
      showAnswer: () => revealAnswerRef.current(),
    });
    return () => onRegisterLessonFooterActions(null);
  }, [onRegisterLessonFooterActions, finished, allPlaced, checkPlacements]);

  // ── Guard ───────────────────────────────────────────────────────────────
  if (!sceneOk) {
    return (
      <div style={{ padding: '8px 0' }}>
        <p style={{ fontSize: 14, color: 'var(--color-text-soft, var(--color-text))', opacity: 0.7 }}>
          This activity is missing scene zones or pieces.
        </p>
      </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        gap: 6,
        padding: '4px 0',
        position: 'relative',
        ...(guided.contentReserveStyle || {}),
      }}
    >
      <style>{`
        @keyframes cme-zone-pulse {
          0%, 100% { box-shadow: 0 0 0 0 ${ACCENT_RING}; }
          50%       { box-shadow: 0 0 0 5px ${ACCENT_RING}; }
        }
      `}</style>

      <CelebrationOverlay visible={showCelebration} message={victoryMsg} />
      <A11yAnnouncement message={a11yMsg} />
      <GuidedHintOverlay
        open={guided.open}
        isNarrow={guided.isNarrow}
        visibleLevel={guided.visibleLevel}
        maxLevel={guided.maxLevel}
        isInteractionPhase={guided.isInteractionPhase}
        anotherClueEnabled={guided.anotherClueEnabled}
        hintEntry={guided.hintEntry}
        showHowAvailable={guided.showHowAvailable}
        resolvedExplanation={guided.resolvedExplanation}
        explanationOpen={guided.explanationOpen}
        onDismiss={guided.dismissOverlay}
        onAnotherClue={guided.anotherClue}
        onReadAloud={() => guidedHintReadAloud(guided.hintEntry, activityId, guided.readAloudTrackLevel)}
        onShowHow={guided.openExplanation}
      />
      <GuidedHintHelpButton visible={guided.showHelpButton} onClick={guided.requestHintManually} />

      {showTitle ? (
        <h2 style={{ margin: '0 0 4px', fontSize: 'clamp(1rem, 3vw, 1.18rem)', fontWeight: 800, color: 'var(--color-text)', lineHeight: 1.22 }}>
          {title}
        </h2>
      ) : null}

      {/* Show instructions only outside guided lesson context */}
      {instructions && !singleSurface ? (
        <p style={{ margin: '0 0 6px', fontSize: 14, fontWeight: 600, color: 'var(--color-text)', lineHeight: 1.4, opacity: 0.88 }}>
          {instructions}
        </p>
      ) : null}

      {/* ── ZONES GRID ── */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: zones.length <= 2 ? '1fr' : 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 6,
        }}
      >
        {zones.map((zone) => {
          const animalsInZone = pieces.filter((p) => placed[p.id] === zone.id);
          const animalHere = !multiplePerZone ? animalsInZone[0] : null;
          const hasAnimals = animalsInZone.length > 0;
          const isTarget = !!selectedAnimal && !finished;
          const isWrong = multiplePerZone
            ? animalsInZone.some((a) => wrongPieceIds.has(a.id))
            : !!(animalHere && wrongPieceIds.has(animalHere.id));
          const isCorrect = finished && hasAnimals;
          const bgColor = getZoneBgColor(zone, backgrounds);

          return (
            <div
              key={zone.id}
              role="button"
              tabIndex={isTarget ? 0 : -1}
              aria-label={
                isTarget
                  ? `Place animal in ${zone.label}`
                  : hasAnimals
                  ? `${zone.label}: ${animalsInZone.map((a) => a.label).join(', ')} placed here`
                  : `${zone.label}: empty`
              }
              onPointerUp={(e) => { e.stopPropagation(); handleZoneTap(zone.id); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleZoneTap(zone.id); } }}
              style={{
                position: 'relative',
                borderRadius: 10,
                minHeight: 80,
                background: bgColor,
                border: isWrong
                  ? '2px solid rgba(239,68,68,0.7)'
                  : isCorrect
                  ? '2px solid rgba(34,197,94,0.5)'
                  : isTarget
                  ? `2px dashed ${ACCENT_DASHED}`
                  : '1.5px solid rgba(0,0,0,0.1)',
                cursor: finished ? 'default' : 'pointer',
                touchAction: 'manipulation',
                userSelect: 'none',
                animation: isTarget ? 'cme-zone-pulse 1.4s ease-in-out infinite' : 'none',
                boxSizing: 'border-box',
                transition: 'border 0.15s, background 0.15s',
                overflow: 'hidden',
              }}
            >
              {/* Zone label pill */}
              <div
                style={{
                  position: 'absolute',
                  top: 7,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  fontSize: 11,
                  fontWeight: 800,
                  padding: '2px 9px',
                  borderRadius: 999,
                  background: 'rgba(255,255,255,0.88)',
                  color: '#0f172a',
                  border: '1px solid rgba(15,23,42,0.12)',
                  whiteSpace: 'nowrap',
                  pointerEvents: 'none',
                  userSelect: 'none',
                }}
              >
                {zone.label}
              </div>

              {/* Placed animals */}
              {multiplePerZone ? (
                hasAnimals ? (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: 8,
                      left: 0,
                      right: 0,
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 4,
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: '0 8px',
                    }}
                  >
                    {animalsInZone.map((animal) => (
                      <button
                        key={animal.id}
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleTrayTap(animal.id); }}
                        aria-label={`${animal.label} — tap to return to tray`}
                        style={{
                          width: 36,
                          height: 36,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'none',
                          border: 'none',
                          cursor: finished ? 'default' : 'pointer',
                          padding: 0,
                          pointerEvents: finished ? 'none' : 'auto',
                        }}
                      >
                        <span
                          style={{
                            fontSize: 28,
                            lineHeight: 1,
                            filter: wrongPieceIds.has(animal.id)
                              ? 'drop-shadow(0 0 3px rgba(239,68,68,0.6))'
                              : finished
                              ? 'drop-shadow(0 0 3px rgba(34,197,94,0.5))'
                              : 'none',
                          }}
                          aria-hidden
                        >
                          {animal.emoji || '⬤'}
                        </span>
                      </button>
                    ))}
                  </div>
                ) : null
              ) : (
                animalHere ? (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleTrayTap(animalHere.id); }}
                    aria-label={`${animalHere.label} — tap to return to tray`}
                    style={{
                      position: 'absolute',
                      bottom: 8,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 2,
                      background: 'none',
                      border: 'none',
                      cursor: finished ? 'default' : 'pointer',
                      padding: 0,
                      pointerEvents: finished ? 'none' : 'auto',
                    }}
                  >
                    <span
                      style={{
                        fontSize: 34,
                        lineHeight: 1,
                        filter: isWrong
                          ? 'drop-shadow(0 0 3px rgba(239,68,68,0.6))'
                          : isCorrect
                          ? 'drop-shadow(0 0 3px rgba(34,197,94,0.5))'
                          : 'none',
                      }}
                      aria-hidden
                    >
                      {animalHere.emoji || '⬤'}
                    </span>
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        padding: '1px 6px',
                        borderRadius: 999,
                        background: 'rgba(255,255,255,0.85)',
                        color: '#0f172a',
                        whiteSpace: 'nowrap',
                        maxWidth: 80,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {animalHere.label}
                    </span>
                  </button>
                ) : null
              )}
            </div>
          );
        })}
      </div>

      {/* ── ANIMAL TRAY ── */}
      <div
        role="group"
        aria-label="Animals to place"
        style={{
          flexShrink: 0,
          minHeight: 72,
          padding: '8px 12px',
          display: 'flex',
          gap: 8,
          justifyContent: 'center',
          alignItems: 'center',
          background: 'var(--color-bg)',
          borderTop: '0.5px solid var(--color-border)',
          borderRadius: '0 0 10px 10px',
        }}
      >
        {pieces.map((piece) => {
          const isPlaced = !!placed[piece.id];
          const isSelected = selectedAnimal === piece.id;
          return (
            <button
              key={piece.id}
              type="button"
              aria-label={
                isPlaced
                  ? `${piece.label} — placed in habitat (tap to return)`
                  : isSelected
                  ? `${piece.label} selected — tap a habitat zone`
                  : `Select ${piece.label}`
              }
              aria-pressed={isSelected}
              onClick={() => handleTrayTap(piece.id)}
              style={{
                width: 52,
                height: 52,
                borderRadius: '50%',
                cursor: finished ? 'default' : 'pointer',
                border: isSelected ? `2.5px solid ${ACCENT}` : '2.5px solid transparent',
                transform: isSelected ? 'scale(1.15)' : 'scale(1)',
                boxShadow: isSelected ? `0 0 0 3px ${ACCENT_RING}` : 'none',
                transition: 'all 0.15s',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                background: isPlaced ? 'transparent' : 'rgba(255,255,255,0.7)',
                opacity: isPlaced ? 0.28 : 1,
                outline: isPlaced ? `1.5px dashed rgba(0,0,0,0.28)` : 'none',
                outlineOffset: -1,
              }}
            >
              {!isPlaced ? (
                <span style={{ fontSize: 28, lineHeight: 1 }} aria-hidden>
                  {piece.emoji || '⬤'}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      {/* Reset — inside card, below tray */}
      {Object.keys(placed).length > 0 && !finished ? (
        <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: 2 }}>
          <button
            type="button"
            style={{ ...secondaryBtn, width: 'auto', minHeight: 32, padding: '4px 12px', fontSize: 12 }}
            onClick={resetAll}
          >
            Reset
          </button>
        </div>
      ) : null}

      {/* ── FEEDBACK ── */}
      {lastCheck === 'wrong' && !finished ? (
        <p style={{ ...feedbackIncorrect, marginTop: 6, fontSize: 13 }} role="status">
          Not quite — adjust the animals and try again.
        </p>
      ) : null}
      {finished ? (
        <p style={{ ...feedbackCorrect, marginTop: 6 }} role="status">
          ✓ {victoryMsg}
        </p>
      ) : null}

      {/* Standalone (no footer) check button */}
      {!onRegisterLessonFooterActions && !finished ? (
        <button
          type="button"
          onClick={checkPlacements}
          disabled={!allPlaced}
          style={{
            marginTop: 10,
            minHeight: 48,
            padding: '12px 18px',
            borderRadius: 14,
            border: 'none',
            fontWeight: 700,
            fontSize: 16,
            cursor: allPlaced ? 'pointer' : 'not-allowed',
            background: allPlaced ? 'var(--color-primary)' : 'var(--color-border)',
            color: '#fff',
            width: '100%',
            opacity: allPlaced ? 1 : 0.6,
          }}
        >
          Check answer
        </button>
      ) : null}
    </div>
  );
}
